/** One table in a PDF report. */
export interface PdfSection {
  heading: string;
  columns: string[];
  rows: (string | number)[][];
  /** Shown instead of an empty table. */
  emptyText?: string;
}

export interface PdfReport {
  title: string;
  /** Lines under the title, e.g. who it is for and when it was generated. */
  meta: string[];
  sections: PdfSection[];
  fileName: string;
}

/**
 * Build a PDF report of tables and download it. jsPDF is loaded on demand so
 * it is only fetched when someone actually exports.
 */
export async function downloadPdfReport(report: PdfReport): Promise<void> {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(15, 23, 42);
  doc.text(report.title, margin, 56);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  let y = 74;
  for (const line of report.meta) {
    doc.text(line, margin, y);
    y += 13;
  }
  y += 10;

  for (const section of report.sections) {
    if (y > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      y = 56;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(section.heading, margin, y);
    y += 8;

    if (section.rows.length === 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(section.emptyText ?? 'No data.', margin, y + 12);
      y += 34;
      continue;
    }

    autoTable(doc, {
      startY: y,
      head: [section.columns],
      body: section.rows.map((r) => r.map((c) => String(c))),
      margin: { left: margin, right: margin },
      tableWidth: pageWidth - margin * 2,
      styles: { fontSize: 8.5, cellPadding: 5, overflow: 'linebreak', textColor: [30, 41, 59] },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 251, 252] },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 24;
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${pages}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 24, { align: 'right' });
  }

  doc.save(report.fileName);
}
